import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
import { defaultOnError } from "util/conditional/onError";
import { MAX_POLL_ATTEMPTS, POLL_INTERVAL_MS } from "../../warm-tier-repositories/constants";
const urlPrefix = "/plugins/org.graylog.plugins.datatiering/datatiering/repositories";
const DATA_TIERING_REPOSITORIES_KEY = "repositories";
const DATA_TIERING_FS_PATHS_KEY = "fs_paths";
const hasUnknownSignal = (repositories) => (repositories != null ? repositories : []).some(
  (repository) => repository.status === "unknown" || repository.credential_status === "unknown"
);
const createRepository = (repository) => fetch("POST", qualifyUrl(`${urlPrefix}`), repository);
const updateRepository = (repository) => fetch("PUT", qualifyUrl(`${urlPrefix}/${repository.name}`), repository);
const deleteRepository = (name) => fetch("DELETE", qualifyUrl(`${urlPrefix}/${name}`));
const listRepositories = (type) => {
  const types = (Array.isArray(type) ? type : [type]).filter(Boolean);
  const query = types.map((t) => `type=${encodeURIComponent(t)}`).join("&");
  const url = query ? `${urlPrefix}?${query}` : urlPrefix;
  return fetch("GET", qualifyUrl(url));
};
const listFsPaths = () => fetch("GET", qualifyUrl(`${urlPrefix}/fs-paths`));
const useRepositories = (type, { fetchFsPaths = false, enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const [pollAttempts, setPollAttempts] = useState(0);
  const {
    data: dataRepositories,
    isLoading: isLoadingRepositories,
    dataUpdatedAt: repositoriesUpdatedAt,
    refetch: refetchRepositories
  } = useQuery({
    queryKey: [DATA_TIERING_REPOSITORIES_KEY, type],
    enabled,
    refetchInterval: (query) => hasUnknownSignal(query.state.data) && pollAttempts <= MAX_POLL_ATTEMPTS ? POLL_INTERVAL_MS : false,
    queryFn: async () => {
      const repositories = await defaultOnError(
        listRepositories(type),
        "Loading repositories failed with status",
        "Could not load repositories."
      );
      setPollAttempts((attempts) => hasUnknownSignal(repositories) ? attempts + 1 : 0);
      return repositories;
    }
  });
  const checksOutstanding = hasUnknownSignal(dataRepositories);
  const { data: dataFsPaths, isLoading: isLoadingFsPaths } = useQuery({
    queryKey: [DATA_TIERING_FS_PATHS_KEY],
    enabled: fetchFsPaths,
    queryFn: () => defaultOnError(
      listFsPaths(),
      "Loading file system paths failed with status",
      "Could not load file system paths."
    )
  });
  const { mutateAsync: onCreateRepository } = useMutation({
    mutationFn: createRepository,
    onSuccess: () => {
      UserNotification.success("Repository created successfully.");
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    },
    onError: (error) => {
      var _a, _b, _c;
      const backendErrors = (_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors;
      if (((_c = error == null ? void 0 : error.additional) == null ? void 0 : _c.status) === 409) {
        UserNotification.error("Another request is already creating or updating this repository. Please try again.");
      } else if (typeof backendErrors !== "object" || backendErrors === null) {
        UserNotification.error(`Repository creation failed with error: ${error}.`);
      }
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    }
  });
  const { mutateAsync: onUpdateRepository } = useMutation({
    mutationFn: updateRepository,
    onSuccess: () => {
      UserNotification.success("Repository updated successfully.");
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    },
    onError: (error) => {
      UserNotification.error(`Updating repository failed with error: ${error}.`);
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    }
  });
  const { mutateAsync: onDeleteRepository } = useMutation({
    mutationFn: deleteRepository,
    onSuccess: () => {
      UserNotification.success("Repository deleted successfully.");
      queryClient.invalidateQueries({
        queryKey: [DATA_TIERING_REPOSITORIES_KEY]
      });
    },
    onError: (error) => {
      UserNotification.error(`Repository deletion failed with error: ${error}.`);
    }
  });
  return {
    fsPaths: {
      data: dataFsPaths,
      isLoading: isLoadingFsPaths
    },
    repositories: {
      data: dataRepositories,
      isLoading: isLoadingRepositories,
      dataUpdatedAt: repositoriesUpdatedAt,
      refetch: refetchRepositories,
      checksExhausted: checksOutstanding && pollAttempts > MAX_POLL_ATTEMPTS
    },
    onCreateRepository,
    onDeleteRepository,
    onUpdateRepository
  };
};
export default useRepositories;
